import React, { FunctionComponent, useState } from "react";
import { StyleSheet, ScrollView, ActivityIndicator } from "react-native";
import { useQuery } from "@apollo/client";
import { get } from "lodash";
import { SafeAreaView } from "react-native-safe-area-context";
import { useSelector } from "react-redux";

import Header from "../../components/Header";
import ProcedureList from "../../components/ProcedureList";
import useCustomer from "../../hooks/useCustomer";
import { RootState } from "../../state";
import { colors } from "../../theme";
import { ServiceType } from "../../types";
import { ageInYears } from "../../utils/customer";
import { QUERY_PROCEDURES } from "./gql";
import { filterServiceTypes } from "../../utils/service";
import Container from "../../components/Container";

// How long the selected-card highlight shows before advancing -- long
// enough to read as "your tap registered", short enough not to feel
// like a stall.
const AUTO_ADVANCE_DELAY_MS = 350;

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.black,
    flex: 1,
  },
  scrollView: {
    paddingHorizontal: 20,
    paddingBottom: 30,
  },
});

interface Props {
  route: any;
  navigation: any;
}

const NewServiceForVisitScreen: FunctionComponent<Props> = ({
  route,
  navigation,
}) => {
  const { store } = route.params;
  const [
    selectedServiceType,
    setSelectedServiceType,
  ] = useState<ServiceType | null>(null);

  const { id: subjectId } = useSelector(
    (state: RootState) => state.consentFlow.subject,
  );

  // The subject for the visit may or may not be the current customer
  // e.g they may have selected a minor
  const subject = useCustomer(subjectId as string);
  const subjectAge = ageInYears(subject?.dateOfBirth);

  const { data, loading } = useQuery(QUERY_PROCEDURES, {
    variables: { id: store.id, minimumAge: subjectAge },
  });

  const serviceTypes = get(data, "store.serviceTypes", []);
  const filteredServiceTypes = filterServiceTypes(serviceTypes);

  // Auto-advances on selection instead of requiring a separate Next tap
  // right below the just-selected card -- that tight tap-then-tap
  // sequence was the exact window where iOS Safari could miss the
  // second tap (see [[project-ios-safari-double-tap]] in memory).
  const handleSelect = (serviceType: ServiceType) => {
    setSelectedServiceType(serviceType);
    setTimeout(() => {
      const categories = serviceType.categories || [];

      if (categories.length > 1) {
        navigation.navigate("ServiceCategorySelectionForVisit", {
          ...route.params,
          serviceType,
        });
      } else {
        navigation.navigate("ServiceSelectionForVisit", {
          ...route.params,
          serviceType,
          serviceTypeCategory: categories[0],
        });
      }
    }, AUTO_ADVANCE_DELAY_MS);
  };

  return (
    <SafeAreaView style={styles.container} edges={["right", "bottom", "left"]}>
      <Container>
        <ScrollView style={styles.scrollView}>
          <Header
            title="The Procedure"
            description="Please select what you’re looking to get done (you can select an
            additional procedure at the end)."
          />
          {loading && <ActivityIndicator color={colors.white} size="small" />}
          {!loading && (
            <ProcedureList
              procedures={filteredServiceTypes}
              onSelect={handleSelect}
              selectedProcedure={selectedServiceType}
            />
          )}
        </ScrollView>
      </Container>
    </SafeAreaView>
  );
};

export default NewServiceForVisitScreen;
